package com.routexk.quiz.funnel.ui

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.Composable
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.ui.theme.toRgbString
import com.routexk.quiz.ui.theme.toRgbaString
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

/** Red CTA gradient (171deg, #EF4444 -> #B91C1C) used across all funnel primary buttons. */
fun Modifier.ctaGradient(): Modifier = styleModifier {
    property(
        "background",
        "linear-gradient(171deg, ${AppColors.Funnel.CtaGradientTop.toRgbString()} 0%, " +
            "${AppColors.Funnel.CtaGradientBottom.toRgbString()} 100%)"
    )
}

/** Blue gradient for the "money" stat cards (116deg, #4077ED -> #1E3AD8). */
fun Modifier.moneyGradient(): Modifier = styleModifier {
    property(
        "background",
        "linear-gradient(116deg, ${AppColors.Funnel.MoneyBlueTop.toRgbString()} 1%, " +
            "${AppColors.Funnel.MoneyBlueBottom.toRgbString()} 97%)"
    )
    property("box-shadow", "12px 12px 12px rgba(0, 66, 139, 0.08)")
}

/** Interactive-element helper: role/label/tabindex + Enter/Space activation. */
fun Modifier.clickableRole(role: String, label: String?, onActivate: () -> Unit): Modifier =
    attrsModifier {
        attr("role", role)
        label?.let { attr("aria-label", it) }
        attr("tabindex", "0")
        onKeyDown {
            if (it.key == "Enter" || it.key == " ") {
                it.preventDefault()
                onActivate()
            }
        }
    }

@Composable
fun FunnelLogo(modifier: Modifier = Modifier, height: Int = 20) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        // SVGs export with width/height 100%, so both dimensions must be explicit
        org.jetbrains.compose.web.dom.Img(
            src = "/funnel/logo-mark.svg",
            attrs = {
                attr("alt", "")
                style { width(height.rpx); height(height.rpx); marginRight(5.rpx) }
            }
        )
        org.jetbrains.compose.web.dom.Img(
            src = "/funnel/logo-text.svg",
            attrs = {
                attr("alt", "RouteXK")
                style {
                    width((height * 0.72 * 5.91).rpx)
                    height((height * 0.72).rpx)
                }
            }
        )
    }
}

@Composable
fun FunnelProgressBar(
    progress: Double,
    onBack: (() -> Unit)?,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.fillMaxWidth().gap(4.rpx),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (onBack != null) {
            Box(
                Modifier
                    .size(20.rpx)
                    .flexShrink(0)
                    .cursor(Cursor.Pointer)
                    .onClick { onBack() }
                    .clickableRole("button", "Go back", onBack)
            ) {
                org.jetbrains.compose.web.dom.Img(
                    src = "/funnel/icon-arrow-left.svg",
                    attrs = {
                        attr("alt", "")
                        style { width(100.percent); height(100.percent) }
                    }
                )
            }
        }
        Box(
            Modifier
                .fillMaxWidth()
                .height(4.rpx)
                .borderRadius(2.rpx)
                .backgroundColor(AppColors.Funnel.ProgressTrack)
                .attrsModifier {
                    attr("role", "progressbar")
                    attr("aria-valuemin", "0")
                    attr("aria-valuemax", "100")
                    attr("aria-valuenow", (progress * 100).toInt().toString())
                }
        ) {
            Box(
                Modifier
                    .fillMaxHeight()
                    .width((progress * 100).percent)
                    .borderRadius(2.rpx)
                    .backgroundColor(AppColors.Funnel.ProgressFill)
                    .transition(com.varabyte.kobweb.compose.css.Transition.of("width", 300.ms))
            )
        }
    }
}

@Composable
fun FunnelCta(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    loading: Boolean = false,
    /** Optional trailing icon (e.g. external-link) rendered after the label. */
    trailingIcon: String? = null,
    /** Landing-hero size from the design: 410x76 / 24px on desktop, full-width 84px / 21px on mobile. */
    hero: Boolean = false
) {
    val active = enabled && !loading
    // Mobile design uses a full-width button, slightly taller than the desktop
    // one (56px per the client's "bigger first button" feedback)
    val mobile = com.routexk.quiz.ui.utils.isMobile()
    Box(
        modifier = modifier
            .width(if (mobile) 100.percent else if (hero) 410.rpx else 320.rpx)
            .height(if (mobile) (if (hero) 84.rpx else 56.rpx) else if (hero) 76.rpx else 48.rpx)
            // Hero on mobile: tighter padding so the label fits on one line
            .padding(leftRight = if (hero && mobile) 16.rpx else 48.rpx)
            .borderRadius(100.rpx)
            .then(
                if (active) Modifier.ctaGradient()
                else Modifier.styleModifier {
                    // Disabled: translucent CTA gradient layered over solid
                    // white — looks faded, but scrolling content can't show
                    // through the button
                    property(
                        "background",
                        "linear-gradient(171deg, " +
                            "${AppColors.Funnel.CtaGradientTop.toRgbaString(0.6f)} 0%, " +
                            "${AppColors.Funnel.CtaGradientBottom.toRgbaString(0.6f)} 100%), #FFFFFF"
                    )
                }
            )
            .cursor(if (active) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier { property("box-shadow", "0 8px 24px rgba(229, 50, 45, 0.25)") }
            .onClick { if (active) onClick() }
            .clickableRole("button", text) { if (active) onClick() },
        contentAlignment = Alignment.Center
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.Center, modifier = Modifier.gap(8.rpx)) {
            SpanText(
                if (loading) "..." else text,
                Modifier
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF1, 0xF1, 0xF1))
                    .fontSize(if (hero) (if (mobile) 21.rpx else 24.rpx) else 14.rpx)
                    .lineHeight(20.rpx)
                    .fontWeight(FontWeight.Bold)
                    .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                    .textAlign(TextAlign.Center)
                    .styleModifier {
                        property("text-transform", "uppercase")
                        // The hero label must never wrap to a second line
                        if (hero) property("white-space", "nowrap")
                    }
            )
            if (trailingIcon != null && !loading) {
                org.jetbrains.compose.web.dom.Img(
                    src = trailingIcon,
                    attrs = { attr("aria-hidden", "true"); style { width(18.rpx); height(18.rpx) } }
                )
            }
        }
    }
}

/**
 * Pins the primary CTA to the bottom of the viewport so it stays reachable
 * while the page scrolls (mobile and desktop alike). Emits a spacer in the
 * normal flow so the last content is never hidden behind the fixed button.
 */
@Composable
fun StickyCtaBar(
    /** Height of the in-flow spacer; raise it when the bar holds more than the button. */
    spacerHeight: Int? = null,
    content: @Composable () -> Unit
) {
    val mobile = com.routexk.quiz.ui.utils.isMobile()
    Box(Modifier.fillMaxWidth().height((spacerHeight ?: if (mobile) 72 else 68).rpx))
    Box(
        Modifier.styleModifier {
            property("position", "fixed")
            property("left", "16px")
            property("right", "16px")
            property("bottom", "16px")
            property("z-index", "20")
            // Let clicks pass through the full-width strip around the button
            property("pointer-events", "none")
        },
        contentAlignment = Alignment.Center
    ) {
        Box(
            Modifier
                .styleModifier { property("pointer-events", "auto") }
                .then(if (mobile) Modifier.fillMaxWidth() else Modifier)
        ) {
            content()
        }
    }
}

@Composable
fun FunnelCard(
    modifier: Modifier = Modifier,
    padding: com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue = 24.rpx,
    radius: com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue = 20.rpx,
    content: @Composable () -> Unit
) {
    Box(
        modifier = modifier
            .backgroundColor(Colors.White)
            .borderRadius(radius)
            .border(1.rpx, LineStyle.Solid, AppColors.Funnel.CardBorder)
            .padding(padding)
            .styleModifier { property("box-shadow", AppColors.Funnel.CardShadow) }
    ) {
        content()
    }
}

@Composable
fun QuizOptionList(
    options: List<String>,
    selectedIndex: Int?,
    onSelect: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    FunnelCard(modifier = modifier.fillMaxWidth(), padding = 8.rpx) {
        Column(
            Modifier.fillMaxWidth().attrsModifier { attr("role", "radiogroup") }
        ) {
            options.forEachIndexed { index, option ->
                val selected = index == selectedIndex
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(topBottom = 18.rpx, leftRight = 20.rpx)
                        .borderRadius(14.rpx)
                        .cursor(Cursor.Pointer)
                        .border(
                            1.rpx,
                            LineStyle.Solid,
                            if (selected) AppColors.Primary else Colors.Transparent
                        )
                        .backgroundColor(
                            if (selected) AppColors.Funnel.SelectedTint else Colors.Transparent
                        )
                        .onClick { onSelect(index) }
                        .attrsModifier {
                            attr("role", "radio")
                            attr("aria-checked", selected.toString())
                            attr("tabindex", "0")
                            onKeyDown {
                                if (it.key == "Enter" || it.key == " ") {
                                    it.preventDefault()
                                    onSelect(index)
                                }
                            }
                        },
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        option,
                        Modifier
                            .fontSize(16.rpx)
                            .color(AppColors.Light.TextPrimary)
                    )
                    Box(
                        Modifier
                            .size(20.rpx)
                            .flexShrink(0)
                            .borderRadius(50.percent)
                            .border(2.rpx, LineStyle.Solid, AppColors.Primary),
                        contentAlignment = Alignment.Center
                    ) {
                        if (selected) {
                            Box(
                                Modifier
                                    .size(10.rpx)
                                    .borderRadius(50.percent)
                                    .backgroundColor(AppColors.Primary)
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Small colored status pill, e.g. "ANALYSIS COMPLETE" or "Subscription Activated". */
@Composable
fun StatusBadge(
    text: String,
    modifier: Modifier = Modifier,
    background: com.varabyte.kobweb.compose.ui.graphics.Color = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE8, 0xF6, 0xEB),
    foreground: com.varabyte.kobweb.compose.ui.graphics.Color = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x29, 0x71, 0x29),
    prefix: String = "✔"
) {
    Row(
        modifier = modifier
            .padding(topBottom = 8.rpx, leftRight = 16.rpx)
            .borderRadius(100.rpx)
            .backgroundColor(background)
            .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xB4, 0xE9, 0xB4))
            .gap(6.rpx),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            if (prefix.isEmpty()) text else "$prefix $text",
            Modifier
                .fontSize(14.rpx)
                .lineHeight(16.rpx)
                .fontWeight(FontWeight.SemiBold)
                .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                .styleModifier { property("text-transform", "uppercase") }
                .color(foreground)
        )
    }
}
